import {
    IIndicatorResponse,
    MetricClassificationSourceEnum,
    MetricTypeCodes,
} from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
    DataSourceSort,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import {
    buildOrderBy,
    constructSearchExpression,
    Filter,
    toDataSourceResult,
} from 'utils/query_v3';

export const indicatorDefaultSort: DataSourceSort[] = [
    {
        field: 'SystemSortOrder ',
        dir: 'asc',
    },
    {
        field: 'Id',
        dir: 'asc',
    },
];

export type IndicatorFilter = Readonly<{
    excludeId?: string;
    corporationId?: string;
    locationId?: string;
    groupIds?: string[];
    customFilterIds?: string[];
    indicatorTypes?: MetricTypeCodes[];
    excludeIndicatorTypes?: MetricTypeCodes[];
    sensorRoleTypes?: string[];
    classifications?: MetricClassificationSourceEnum[];
    nameSearch?: string;
}>;

export type IndicatorDataSourceOptions = BaseDataSourceOptions<
    IIndicatorResponse,
    IndicatorFilter
>;

export const getIndicatorFilterExpr = ({
    nameSearch,
    excludeIndicatorTypes,
    sensorRoleTypes,
    corporationId,
    locationId,
    excludeId,
    classifications,
}: IndicatorFilter) => {
    const searchExpr = constructSearchExpression(nameSearch ?? '', ['Name']);

    return Filter.and(
        ...(excludeIndicatorTypes?.map((type) => Filter.neq('IndicatorType', type)) ??
            []),
        sensorRoleTypes && Filter.in('MeasureOf', sensorRoleTypes),
        corporationId && Filter.eq('CorporationId', corporationId),
        locationId && Filter.eq('LocationId', locationId),
        excludeId && Filter.neq('Id', excludeId),
        classifications && Filter.in('Classification', classifications),
        searchExpr,
    );
};

export const getPrometheusIndicators: DataSourceQuery<
    IIndicatorResponse,
    IndicatorFilter
> = ({ filter, sort, page, pageLimit }) => {
    const orderBy = buildOrderBy(sort);

    const filterExpr = getIndicatorFilterExpr(filter);

    return toDataSourceResult(
        sdClient.getIndicators({
            groupIds: filter.groupIds,
            filterIds: filter.customFilterIds,
            indicatorTypeIds: filter.indicatorTypes,
            filter: filterExpr,
            page,
            pageLimit,
            orderBy,
        }),
    );
};

export type IndicatorPagedDataSource = PagedDataSource<
    IIndicatorResponse,
    IndicatorFilter
>;

export function usePagedIndicatorDataSource(
    options: IndicatorDataSourceOptions,
): IndicatorPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: getPrometheusIndicators,
    });
}

export type IndicatorInfiniteDataSource = InfiniteDataSource<
    IIndicatorResponse,
    IndicatorFilter
>;

export function useInfiniteIndicatorDataSource(
    options: IndicatorDataSourceOptions,
): IndicatorInfiniteDataSource {
    return useInfiniteDataSource({
        defaultSort: indicatorDefaultSort,
        ...options,
        query: getPrometheusIndicators,
    });
}
